import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, Music, Calendar, Truck, Store, User, Play, MapPin, Clock, ExternalLink } from 'lucide-react';
import SearchBar from '@/components/SearchBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { usePlayer } from '@/context/PlayerContext';

const SearchPage = () => {
  const [searchResults, setSearchResults] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const { playTrack } = usePlayer();

  const handleResults = useCallback((results) => {
    setSearchResults(results);
    if (results) {
      // Auto-select first tab with results
      if (results.results.artists.length > 0) setActiveTab('artists');
      else if (results.results.events.length > 0) setActiveTab('events');
      else if (results.results.tracks.length > 0) setActiveTab('tracks');
      else if (results.results.food_trucks.length > 0) setActiveTab('food_trucks');
      else if (results.results.vendors.length > 0) setActiveTab('vendors');
      else setActiveTab('all');
    }
  }, []);

  const handlePlayTrack = (track) => {
    playTrack(track, { name: track.artist_name || 'Unknown Artist' });
  };

  const getCategoryCount = (category) => {
    if (!searchResults) return 0;
    return searchResults.results[category]?.length || 0;
  };

  const totalResults = searchResults?.total || 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
        <div className="flex items-center gap-2">
          <Search size={16} className="text-primary" />
          <span className="text-xs text-muted-foreground tracking-widest uppercase">Discover Twin Cities</span>
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Search</h1>
        <p className="text-muted-foreground max-w-2xl">
          Find artists, events, food trucks, vendors, and more across the Twin Cities.
        </p>
      </motion.div>

      {/* Search Bar */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <SearchBar onResults={handleResults} />
      </motion.div>

      {/* Results */}
      <AnimatePresence mode="wait">
        {searchResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Results Summary */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {totalResults} result{totalResults !== 1 ? 's' : ''} found
                {searchResults.query && ` for "${searchResults.query}"`}
              </p>
            </div>

            {totalResults === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Search size={48} className="text-muted-foreground mb-4" />
                  <h3 className="font-heading text-xl font-bold mb-2">No results found</h3>
                  <p className="text-muted-foreground text-center">
                    Try adjusting your search or filters
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="flex-wrap h-auto gap-1 p-1">
                  <TabsTrigger value="all" className="gap-1">
                    All <Badge variant="secondary" className="ml-1">{totalResults}</Badge>
                  </TabsTrigger>
                  {getCategoryCount('artists') > 0 && (
                    <TabsTrigger value="artists" className="gap-1">
                      <User size={14} /> Artists <Badge variant="secondary" className="ml-1">{getCategoryCount('artists')}</Badge>
                    </TabsTrigger>
                  )}
                  {getCategoryCount('events') > 0 && (
                    <TabsTrigger value="events" className="gap-1">
                      <Calendar size={14} /> Events <Badge variant="secondary" className="ml-1">{getCategoryCount('events')}</Badge>
                    </TabsTrigger>
                  )}
                  {getCategoryCount('tracks') > 0 && (
                    <TabsTrigger value="tracks" className="gap-1">
                      <Music size={14} /> Music <Badge variant="secondary" className="ml-1">{getCategoryCount('tracks')}</Badge>
                    </TabsTrigger>
                  )}
                  {getCategoryCount('food_trucks') > 0 && (
                    <TabsTrigger value="food_trucks" className="gap-1">
                      <Truck size={14} /> Food <Badge variant="secondary" className="ml-1">{getCategoryCount('food_trucks')}</Badge>
                    </TabsTrigger>
                  )}
                  {getCategoryCount('vendors') > 0 && (
                    <TabsTrigger value="vendors" className="gap-1">
                      <Store size={14} /> Vendors <Badge variant="secondary" className="ml-1">{getCategoryCount('vendors')}</Badge>
                    </TabsTrigger>
                  )}
                </TabsList>

                {/* ALL TAB */}
                <TabsContent value="all" className="space-y-8 mt-6">
                  {/* Artists Preview */}
                  {searchResults.results.artists.length > 0 && (
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                          <User size={18} className="text-primary" /> Artists
                        </h2>
                        {searchResults.results.artists.length > 4 && (
                          <Button variant="ghost" size="sm" onClick={() => setActiveTab('artists')}>View all</Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {searchResults.results.artists.slice(0, 4).map((artist) => (
                          <Link to={`/artist/${artist.id}`} key={artist.id}>
                            <Card className="overflow-hidden hover:border-primary transition-colors">
                              <div className="aspect-square">
                                <img src={artist.image_url || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400'} alt={artist.name} className="w-full h-full object-cover" />
                              </div>
                              <CardContent className="p-3">
                                <p className="font-medium truncate">{artist.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{artist.genre}</p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Events Preview */}
                  {searchResults.results.events.length > 0 && (
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                          <Calendar size={18} className="text-secondary" /> Events
                        </h2>
                        {searchResults.results.events.length > 3 && (
                          <Button variant="ghost" size="sm" onClick={() => setActiveTab('events')}>View all</Button>
                        )}
                      </div>
                      <div className="space-y-3">
                        {searchResults.results.events.slice(0, 3).map((event) => (
                          <Card key={event.id}>
                            <CardContent className="p-4 flex items-center gap-4">
                              <div className="w-16 h-16 rounded bg-primary/10 flex flex-col items-center justify-center">
                                <span className="text-lg font-bold">{new Date(event.date).getDate()}</span>
                                <span className="text-xs text-muted-foreground">{new Date(event.date).toLocaleString('default', { month: 'short' })}</span>
                              </div>
                              <div className="flex-1">
                                <p className="font-medium">{event.title}</p>
                                <p className="text-sm text-muted-foreground flex items-center gap-2">
                                  <MapPin size={12} />{event.venue}
                                  <Clock size={12} className="ml-2" />{event.time}
                                </p>
                              </div>
                              {event.price_tier && <Badge variant="outline">{event.price_tier}</Badge>}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Tracks Preview */}
                  {searchResults.results.tracks.length > 0 && (
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                          <Music size={18} className="text-purple-500" /> Music
                        </h2>
                        {searchResults.results.tracks.length > 4 && (
                          <Button variant="ghost" size="sm" onClick={() => setActiveTab('tracks')}>View all</Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {searchResults.results.tracks.slice(0, 4).map((track) => (
                          <Card key={track.id} className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => handlePlayTrack(track)}>
                            <CardContent className="p-3 flex items-center gap-3">
                              <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                                <Play size={20} className="text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{track.title}</p>
                                <p className="text-sm text-muted-foreground truncate">{track.artist_name}</p>
                              </div>
                              {track.genre && <Badge variant="secondary" className="hidden sm:flex">{track.genre}</Badge>}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </section>
                  )}
                </TabsContent>

                {/* ARTISTS TAB */}
                <TabsContent value="artists" className="mt-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {searchResults.results.artists.map((artist) => (
                      <Link to={`/artist/${artist.id}`} key={artist.id}>
                        <Card className="overflow-hidden hover:border-primary transition-colors h-full">
                          <div className="aspect-square">
                            <img src={artist.image_url || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400'} alt={artist.name} className="w-full h-full object-cover" />
                          </div>
                          <CardContent className="p-4">
                            <p className="font-heading font-bold truncate">{artist.name}</p>
                            <p className="text-sm text-muted-foreground truncate">{artist.genre}</p>
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{artist.bio}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>

                {/* EVENTS TAB */}
                <TabsContent value="events" className="mt-6">
                  <div className="space-y-4">
                    {searchResults.results.events.map((event) => (
                      <Card key={event.id}>
                        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="w-20 h-20 rounded-sm bg-primary/10 flex flex-col items-center justify-center flex-shrink-0">
                            <span className="text-2xl font-bold">{new Date(event.date).getDate()}</span>
                            <span className="text-sm text-muted-foreground">{new Date(event.date).toLocaleString('default', { month: 'short' })}</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-heading text-lg font-bold">{event.title}</h3>
                            <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-2 mt-1">
                              <span className="flex items-center gap-1"><MapPin size={12} />{event.venue}</span>
                              <span className="flex items-center gap-1"><Clock size={12} />{event.time}</span>
                            </p>
                            {event.description && <p className="text-sm mt-2 line-clamp-2">{event.description}</p>}
                            <div className="flex items-center gap-2 mt-2">
                              {event.price_tier && <Badge variant="outline">{event.price_tier}</Badge>}
                              {event.genre && <Badge variant="secondary">{event.genre}</Badge>}
                              {event.vibe && <Badge variant="secondary">{event.vibe}</Badge>}
                            </div>
                          </div>
                          {event.ticket_url && (
                            <Button variant="outline" size="sm" onClick={() => window.open(event.ticket_url, '_blank')} className="gap-1">
                              <ExternalLink size={14} /> Tickets
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* TRACKS TAB */}
                <TabsContent value="tracks" className="mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {searchResults.results.tracks.map((track) => (
                      <Card key={track.id} className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => handlePlayTrack(track)}>
                        <CardContent className="p-4 flex items-center gap-4">
                          <div className="w-16 h-16 rounded bg-muted flex items-center justify-center flex-shrink-0">
                            {track.thumbnail ? (
                              <img src={track.thumbnail} alt={track.title} className="w-full h-full object-cover rounded" />
                            ) : (
                              <Play size={24} className="text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-heading font-bold truncate">{track.title}</p>
                            <p className="text-sm text-muted-foreground truncate">{track.artist_name}</p>
                            {track.genre && <Badge variant="secondary" className="mt-1">{track.genre}</Badge>}
                          </div>
                          <Button size="icon" variant="ghost"><Play size={20} /></Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* FOOD TRUCKS TAB */}
                <TabsContent value="food_trucks" className="mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {searchResults.results.food_trucks.map((truck) => (
                      <Card key={truck.id} className="overflow-hidden">
                        <div className="aspect-video">
                          <img src={truck.image_url || 'https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=400'} alt={truck.name} className="w-full h-full object-cover" />
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-heading font-bold">{truck.name}</h3>
                          <Badge variant="secondary" className="mt-1">{truck.cuisine}</Badge>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{truck.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* VENDORS TAB */}
                <TabsContent value="vendors" className="mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {searchResults.results.vendors.map((vendor) => (
                      <Card key={vendor.id} className="overflow-hidden">
                        <div className="aspect-video">
                          <img src={vendor.image_url || 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400'} alt={vendor.name} className="w-full h-full object-cover" />
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-heading font-bold">{vendor.name}</h3>
                          <Badge variant="secondary" className="mt-1">{vendor.category}</Badge>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{vendor.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Initial State */}
      {!searchResults && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <Search size={64} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading text-xl font-bold mb-2">Start Searching</h3>
          <p className="text-muted-foreground">
            Search for artists, events, food trucks, vendors, and more
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default SearchPage;
